import { useTranslation } from 'react-i18next'

import { ProhibitIcon } from '@phosphor-icons/react'

import { Card, CardTitle, shipmentStatuses, Text, theme } from '@reveni-io/reveni-ui'

import CustomerRating from '@/common/components/CustomerRating/CustomerRating'
import ParcelEta from '@/parcels/components/ParcelEta/ParcelEta'
import ParcelTrackingNumber from '@/parcels/components/ParcelTrackingNumber/ParcelTrackingNumber'
import { parcelStatusesByDirection, parcelStatusInfo } from '@/parcels/constants'

import ParcelPickupDate from './ParcelPickupDate/ParcelPickupDate'
import StatusTooltip from './StatusTooltip/StatusTooltip'
import * as Styles from './styles'

const ParcelStatus = ({
  direction,
  parcelStatus,
  createdAt,
  packedAt,
  readyAt,
  shippedAt,
  outForDeliveryAt,
  deliveredAt,
  timeZone,
  normalizedTrackingStatus,
  trackingNumber,
  trackingUrl,
  customerReviewRating,
  pickupDate,
  eta,
}) => {
  const { t, i18n } = useTranslation()

  const statuses = parcelStatusesByDirection[direction]?.map(status => [status, parcelStatusInfo[status]])

  const isParcelStatusDone = (parcelStatus, status) => {
    const parcelStatusIndex = statuses.findIndex(([value]) => value === parcelStatus)
    const statusIndex = statuses.findIndex(([value]) => value === status)

    return statusIndex < parcelStatusIndex
  }

  const isParcelStatusActive = (parcelStatus, status) => {
    return parcelStatus === status
  }

  const getIconColor = (parcelStatus, status, iconColor) => {
    if (isParcelStatusDone(parcelStatus, status)) {
      return theme.legacy.colors.text.default
    }

    if (isParcelStatusActive(parcelStatus, status)) {
      return iconColor
    }

    return theme.legacy.palette.grey.tone500
  }

  const getDateAndHour = (parcelStatus, status) => {
    let timestamp = null

    if (parcelStatus === shipmentStatuses.requested && status === shipmentStatuses.requested) {
      timestamp = createdAt
    }

    if (parcelStatus === shipmentStatuses.packed && status === shipmentStatuses.packed) {
      timestamp = packedAt
    }

    if (parcelStatus === shipmentStatuses.ready && status === shipmentStatuses.ready) {
      timestamp = readyAt
    }

    if (parcelStatus === shipmentStatuses.shipped && status === shipmentStatuses.shipped) {
      timestamp = shippedAt
    }

    if (parcelStatus === shipmentStatuses.out_for_delivery && status === shipmentStatuses.out_for_delivery) {
      timestamp = outForDeliveryAt
    }

    if (parcelStatus === shipmentStatuses.delivered && status === shipmentStatuses.delivered) {
      timestamp = deliveredAt
    }

    if (!timestamp) return null

    const dateToShow = new Date(timestamp)

    const date = new Intl.DateTimeFormat(i18n, {
      hour: 'numeric',
      minute: 'numeric',
      timeZone,
    }).format(dateToShow)

    const hour = new Intl.DateTimeFormat(i18n, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone,
    }).format(dateToShow)

    return { date, hour }
  }

  const getLabelColor = (parcelStatus, status) => {
    if (isParcelStatusDone(parcelStatus, status) || isParcelStatusActive(parcelStatus, status)) {
      return theme.legacy.colors.text.default
    }

    return theme.legacy.palette.grey.tone500
  }

  if (parcelStatus === shipmentStatuses.cancelled) {
    return (
      <Styles.CancelledContainer>
        <ProhibitIcon size={32} />
        <Text type="titleM">{t('Envío cancelado')}</Text>
      </Styles.CancelledContainer>
    )
  }

  return (
    <Card showBorder={false}>
      <CardTitle>
        {t('Estado del envío')}
        <Styles.TrackingNumberAndMenuContainer>
          <ParcelPickupDate date={pickupDate} />
          <ParcelEta date={eta} withDivider />
          <ParcelTrackingNumber trackingNumber={trackingNumber} trackingUrl={trackingUrl} />
          <CustomerRating rating={customerReviewRating} />
        </Styles.TrackingNumberAndMenuContainer>
      </CardTitle>
      <Styles.MainContainer>
        <Styles.StatusesContainer>
          {statuses?.map(([value, info], index) => (
            <>
              <Styles.StatusContainer key={value}>
                <Styles.IconContainer
                  isDone={isParcelStatusDone(parcelStatus, value)}
                  isActive={isParcelStatusActive(parcelStatus, value)}
                  backgroundColor={info.backgroundColor}
                >
                  <info.Icon size={24} color={getIconColor(parcelStatus, value, info.color)} />
                </Styles.IconContainer>
                <Styles.NameAndDateContainer>
                  <Text type="bodyMBold" color={getLabelColor(parcelStatus, value)}>
                    {t(info.label)}
                  </Text>
                  <Text type="allCapsS" color={getLabelColor(parcelStatus, value, info.color)}>
                    {getDateAndHour(parcelStatus, value)?.date}
                  </Text>
                  <Text type="allCapsS" color={getLabelColor(parcelStatus, value, info.color)}>
                    {getDateAndHour(parcelStatus, value)?.hour}
                  </Text>
                  {value === parcelStatus && (
                    <StatusTooltip
                      status={parcelStatus}
                      createdAt={createdAt}
                      packedAt={packedAt}
                      readyAt={readyAt}
                      shippedAt={shippedAt}
                      outForDeliveryAt={outForDeliveryAt}
                      normalizedTrackingStatus={normalizedTrackingStatus}
                    />
                  )}
                </Styles.NameAndDateContainer>
              </Styles.StatusContainer>
              {index !== statuses.length - 1 && (
                <Styles.StatusDivider isDone={isParcelStatusDone(parcelStatus, value)} />
              )}
            </>
          ))}
        </Styles.StatusesContainer>
      </Styles.MainContainer>
    </Card>
  )
}

export default ParcelStatus
